import { useEffect, useState, useMemo } from "react";
import { api } from "../../lib/auth";
import { toast } from "sonner";
import {
  Banknote, Landmark, ChevronRight, Plus, Trash2, X, Save, Sparkles, Check,
  TrendingUp, TrendingDown, ArrowRightLeft, FileText, Edit3, Search, BookOpen,
} from "lucide-react";

const ACCOUNT_TYPES = [
  { v: "asset",     lbl: "Asset",     tint: "emerald" },
  { v: "liability", lbl: "Liability", tint: "rose" },
  { v: "equity",    lbl: "Equity",    tint: "violet" },
  { v: "revenue",   lbl: "Revenue",   tint: "sky" },
  { v: "expense",   lbl: "Expense",   tint: "amber" },
];

const MAPPING_KEYS = [
  { k: "sales_revenue",         lbl: "Sales Revenue",            hint: "Income from product/service sales", types: ["revenue"] },
  { k: "marketplace_revenue",   lbl: "Marketplace Revenue",      hint: "Income from marketplace orders",     types: ["revenue"] },
  { k: "sales_returns",         lbl: "Sales Returns",            hint: "Refunds & returns",                  types: ["revenue", "expense"] },
  { k: "purchases_inventory",   lbl: "Purchases / Inventory",    hint: "Where stock purchases are booked",   types: ["asset", "expense"] },
  { k: "purchases_returns",     lbl: "Purchases Returns",        hint: "Returns to suppliers",               types: ["expense", "asset"] },
  { k: "default_expense",       lbl: "Default Expense Account",  hint: "Fallback for uncategorised costs",   types: ["expense"] },
  { k: "default_deposit_bank",  lbl: "Default Deposit Bank",     hint: "Where cash deposits land",           types: ["asset"] },
  { k: "accounts_receivable",   lbl: "Accounts Receivable",      hint: "Money owed by customers",            types: ["asset"] },
  { k: "accounts_payable",      lbl: "Accounts Payable",         hint: "Money owed to suppliers",            types: ["liability"] },
  { k: "cash_drawer",           lbl: "Cash Drawer (POS)",        hint: "Float for the POS",                  types: ["asset"] },
];

export default function FinancePage() {
  const [tab, setTab] = useState("dashboard"); // dashboard|coa|banks|mappings|ledger
  return (
    <div data-testid="tenant-finance-page" className="p-6 lg:p-8">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-black tracking-tight">Finance</h1>
          <p className="text-sm text-black/55 mt-1">Chart of accounts, bank accounts, ledger mappings — your accounting foundation.</p>
        </div>
      </div>

      <div className="flex gap-1.5 mb-6 bg-white p-1.5 rounded-2xl border border-black/8 inline-flex overflow-x-auto">
        {[
          ["dashboard", "Overview", BookOpen],
          ["coa",       "Chart of Accounts", FileText],
          ["banks",     "Bank Accounts", Landmark],
          ["mappings",  "Account Mappings", ArrowRightLeft],
          ["ledger",    "Ledger History", TrendingUp],
        ].map(([k, lbl, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`fin-tab-${k}`}
            className={`px-4 py-2 rounded-xl text-sm font-bold inline-flex items-center gap-1.5 whitespace-nowrap transition-colors ${tab === k ? "bg-[#1B4332] text-white" : "text-black/65 hover:bg-black/5"}`}>
            <Icon size={14} /> {lbl}
          </button>
        ))}
      </div>

      {tab === "dashboard" && <Dashboard />}
      {tab === "coa"       && <ChartOfAccounts />}
      {tab === "banks"     && <BankAccounts />}
      {tab === "mappings"  && <AccountMappings />}
      {tab === "ledger"    && <Ledger />}
    </div>
  );
}

// ─────────── Dashboard ───────────
function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/t/finance/summary").then((r) => setS(r.data)).catch(() => setS({})); }, []);
  if (!s) return <div className="text-center py-12 text-black/45">Loading…</div>;
  const t = s.totals || {};
  const tiles = [
    { lbl: "Total Assets",        v: t.asset || 0,     icon: TrendingUp,   tint: "emerald" },
    { lbl: "Total Liabilities",   v: t.liability || 0, icon: TrendingDown, tint: "rose" },
    { lbl: "Total Revenue",       v: t.revenue || 0,   icon: TrendingUp,   tint: "sky" },
    { lbl: "Total Expenses",      v: t.expense || 0,   icon: TrendingDown, tint: "amber" },
    { lbl: "Bank Balance Total",  v: s.bank_balance_total || 0, icon: Landmark, tint: "emerald" },
    { lbl: "Net Profit",          v: s.net_profit || 0, icon: Sparkles,    tint: (s.net_profit || 0) >= 0 ? "emerald" : "rose" },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {tiles.map((tile) => <StatTile key={tile.lbl} {...tile} />)}
      </div>
      <div className="bg-white rounded-2xl border border-black/8 p-6">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles size={18} className="text-[#1FA868]" />
          <div className="font-display font-extrabold text-xl text-black">Quick start</div>
        </div>
        <p className="text-sm text-black/55">No chart of accounts yet? Seed a standard 20-account chart in one click, then map your sales / purchases / expense flows to the right ledgers.</p>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SeedButton />
          <Tip n="2" t="Add bank accounts" d="Track each operational bank under Bank Accounts." />
          <Tip n="3" t="Set mappings" d="Tell Brivo which ledger gets each sale, return or expense." />
        </div>
      </div>
    </div>
  );
}
function Tip({ n, t, d }) {
  return (
    <div className="rounded-xl border border-black/8 p-4">
      <div className="text-[11px] uppercase tracking-wider font-bold text-black/55">Step {n}</div>
      <div className="font-bold text-black mt-1">{t}</div>
      <div className="text-xs text-black/55 mt-1 leading-relaxed">{d}</div>
    </div>
  );
}
function SeedButton() {
  const [busy, setBusy] = useState(false);
  const seed = async () => {
    setBusy(true);
    try {
      const r = await api.post("/t/finance/accounts/seed");
      toast.success(`Seeded ${r.data.inserted} accounts`);
    } catch { toast.error("Could not seed"); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={seed} data-testid="fin-seed-coa" disabled={busy}
      className="rounded-xl bg-[#1FA868] text-white p-4 text-left hover:bg-[#16834D] disabled:opacity-60">
      <div className="text-[11px] uppercase tracking-wider font-bold text-white/85">Step 1</div>
      <div className="font-bold mt-1">{busy ? "Seeding…" : "Seed default chart →"}</div>
      <div className="text-xs text-white/85 mt-1 leading-relaxed">20 standard accounts (Assets, Liabilities, Equity, Revenue, Expense).</div>
    </button>
  );
}

function StatTile({ lbl, v, icon: Icon, tint = "emerald" }) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700",
    rose:    "bg-rose-50 text-rose-700",
    sky:     "bg-sky-50 text-sky-700",
    amber:   "bg-amber-50 text-amber-700",
    violet:  "bg-violet-50 text-violet-700",
  };
  return (
    <div className="bg-white rounded-2xl border border-black/8 p-5">
      <div className={`w-10 h-10 rounded-xl ${map[tint] || map.emerald} flex items-center justify-center`}><Icon size={17} /></div>
      <div className="mt-3 text-xs font-bold uppercase tracking-wider text-black/55">{lbl}</div>
      <div className="font-display font-extrabold text-2xl text-black mt-1">₦{Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
    </div>
  );
}

// ─────────── Chart of Accounts ───────────
function ChartOfAccounts() {
  const [accounts, setAccounts] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const reload = () => {
    setAccounts(null);
    api.get("/t/finance/accounts").then((r) => setAccounts(r.data || [])).catch(() => setAccounts([]));
  };
  useEffect(reload, []);

  const filtered = useMemo(() => {
    const list = accounts || [];
    if (!q.trim()) return list;
    const k = q.toLowerCase();
    return list.filter((a) => a.code.toLowerCase().includes(k) || a.name.toLowerCase().includes(k) || a.type.includes(k));
  }, [accounts, q]);

  const remove = async (aid) => {
    if (!window.confirm("Delete this account? This cannot be undone.")) return;
    try { await api.delete(`/t/finance/accounts/${aid}`); toast.success("Deleted"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/45" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="fin-coa-search"
            placeholder="Search by code, name, type…"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]" />
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="fin-coa-add"
          className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold text-sm inline-flex items-center gap-2 shadow">
          <Plus size={15} /> Add account
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
        {accounts === null ? (
          <div className="p-14 text-center text-black/45">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center">
            <FileText size={28} className="mx-auto text-black/25 mb-2" />
            <div className="font-bold text-black">{accounts.length === 0 ? "No accounts yet" : "No matches"}</div>
            <div className="text-xs text-black/55 mt-1">{accounts.length === 0 ? "Seed the default chart of accounts from the Overview tab." : "Try a different search."}</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAFBFB] text-[11px] font-bold uppercase tracking-wider text-black/55">
              <tr>
                <th className="text-left px-5 py-3">Code</th>
                <th className="text-left px-5 py-3">Name</th>
                <th className="text-left px-5 py-3">Type</th>
                <th className="text-right px-5 py-3">Balance</th>
                <th className="text-right px-5 py-3 w-32">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/6">
              {filtered.map((a) => (
                <tr key={a.id} data-testid={`fin-coa-row-${a.code}`}>
                  <td className="px-5 py-3 font-mono font-bold text-black">{a.code}</td>
                  <td className="px-5 py-3">{a.name}{a.is_bank && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-sky-50 text-sky-700">Bank</span>}</td>
                  <td className="px-5 py-3"><TypePill v={a.type} /></td>
                  <td className="px-5 py-3 text-right font-mono">₦{Number(a.balance || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => { setEditing(a); setShowForm(true); }} data-testid={`fin-coa-edit-${a.code}`}
                      className="p-1.5 rounded-lg hover:bg-black/5 text-black/65"><Edit3 size={13} /></button>
                    <button onClick={() => remove(a.id)} data-testid={`fin-coa-delete-${a.code}`}
                      className="p-1.5 rounded-lg hover:bg-rose-50 text-black/65 hover:text-rose-700 ml-1"><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && <AccountForm account={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); reload(); }} />}
    </div>
  );
}

function TypePill({ v }) {
  const t = ACCOUNT_TYPES.find((x) => x.v === v);
  const map = { emerald: "bg-emerald-50 text-emerald-700", rose: "bg-rose-50 text-rose-700", violet: "bg-violet-50 text-violet-700", sky: "bg-sky-50 text-sky-700", amber: "bg-amber-50 text-amber-700" };
  if (!t) return <span className="text-xs text-black/55">{v}</span>;
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${map[t.tint]}`}>{t.lbl}</span>;
}

function AccountForm({ account, onClose, onSaved }) {
  const editing = !!account;
  const [f, setF] = useState({
    code: account?.code || "",
    name: account?.name || "",
    type: account?.type || "asset",
    opening_balance: account?.opening_balance || 0,
    is_bank: account?.is_bank || false,
    description: account?.description || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!f.code.trim() || !f.name.trim()) { toast.error("Code and name are required"); return; }
    setBusy(true);
    try {
      if (editing) await api.patch(`/t/finance/accounts/${account.id}`, f);
      else await api.post("/t/finance/accounts", f);
      toast.success(editing ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-black/8">
          <div className="font-display font-bold text-xl">{editing ? "Edit account" : "New account"}</div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/5"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3.5">
          <div className="grid grid-cols-3 gap-3">
            <Field lbl="Code *">
              <input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} data-testid="fin-coa-form-code"
                placeholder="e.g. 4000" className="input" />
            </Field>
            <Field lbl="Name *" colSpan={2}>
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="fin-coa-form-name"
                placeholder="e.g. Sales Revenue" className="input" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field lbl="Type">
              <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="fin-coa-form-type" className="input">
                {ACCOUNT_TYPES.map((t) => <option key={t.v} value={t.v}>{t.lbl}</option>)}
              </select>
            </Field>
            <Field lbl="Opening balance">
              <input type="number" step="0.01" value={f.opening_balance} onChange={(e) => setF({ ...f, opening_balance: Number(e.target.value) })} className="input" />
            </Field>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={f.is_bank} onChange={(e) => setF({ ...f, is_bank: e.target.checked })} className="w-4 h-4" />
            <span className="text-sm text-black/85">This is a bank account</span>
          </label>
          <Field lbl="Description">
            <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="input resize-none" />
          </Field>
        </div>
        <div className="px-5 py-4 border-t border-black/8 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-black/5 text-black/65 font-bold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="fin-coa-form-save"
            className="px-5 py-2 rounded-xl bg-[#1FA868] text-white font-bold text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
            <Save size={13} /> {busy ? "Saving…" : (editing ? "Save" : "Create")}
          </button>
        </div>
      </div>
      <style>{`.input{width:100%;padding:0.625rem 0.75rem;border-radius:0.75rem;border:1px solid rgba(0,0,0,0.12);background:white;font-size:0.875rem;outline:none}.input:focus{border-color:#1FA868}`}</style>
    </div>
  );
}

function Field({ lbl, colSpan = 1, children }) {
  return (
    <label className={`block ${colSpan === 2 ? "col-span-2" : ""}`}>
      <span className="block text-[11px] font-bold uppercase tracking-wider text-black/55 mb-1">{lbl}</span>
      {children}
    </label>
  );
}

// ─────────── Bank Accounts ───────────
function BankAccounts() {
  const [banks, setBanks] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const reload = () => {
    setBanks(null);
    api.get("/t/finance/bank-accounts").then((r) => setBanks(r.data || [])).catch(() => setBanks([]));
  };
  useEffect(reload, []);

  const remove = async (bid) => {
    if (!window.confirm("Delete this bank account?")) return;
    try { await api.delete(`/t/finance/bank-accounts/${bid}`); toast.success("Deleted"); reload(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="fin-bank-add"
          className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold text-sm inline-flex items-center gap-2 shadow">
          <Plus size={15} /> Add bank account
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {banks === null && <div className="col-span-full text-center py-12 text-black/45">Loading…</div>}
        {banks !== null && banks.length === 0 && (
          <div className="col-span-full bg-white rounded-2xl border border-dashed border-black/12 p-14 text-center">
            <Landmark size={28} className="mx-auto text-black/25 mb-2" />
            <div className="font-bold text-black">No bank accounts yet</div>
            <div className="text-xs text-black/55 mt-1">Add a bank account to track deposits and reconciliations.</div>
          </div>
        )}
        {(banks || []).map((b) => (
          <div key={b.id} data-testid={`fin-bank-${b.id}`}
            className="bg-white rounded-2xl border border-black/8 p-5 relative group">
            {b.is_default && <span className="absolute top-3 right-3 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800">Default</span>}
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><Landmark size={20} /></div>
            <div className="mt-3 text-xs font-bold uppercase tracking-wider text-black/55">{b.bank_name}</div>
            <div className="font-display font-extrabold text-lg text-black mt-0.5 truncate">{b.account_name}</div>
            <div className="text-xs text-black/55 mt-0.5 font-mono">{b.account_number}</div>
            <div className="mt-3 font-extrabold text-xl text-[#1FA868]">{b.currency || "NGN"} {Number(b.balance || 0).toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => { setEditing(b); setShowForm(true); }} data-testid={`fin-bank-edit-${b.id}`}
                className="flex-1 px-3 py-1.5 rounded-lg bg-black/5 text-black text-xs font-bold inline-flex items-center justify-center gap-1"><Edit3 size={11} /> Edit</button>
              <button onClick={() => remove(b.id)} data-testid={`fin-bank-delete-${b.id}`}
                className="flex-1 px-3 py-1.5 rounded-lg bg-rose-50 text-rose-700 text-xs font-bold inline-flex items-center justify-center gap-1"><Trash2 size={11} /> Delete</button>
            </div>
          </div>
        ))}
      </div>

      {showForm && <BankForm bank={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); reload(); }} />}
    </div>
  );
}

function BankForm({ bank, onClose, onSaved }) {
  const editing = !!bank;
  const [f, setF] = useState({
    bank_name: bank?.bank_name || "", account_name: bank?.account_name || "",
    account_number: bank?.account_number || "", currency: bank?.currency || "NGN",
    branch: bank?.branch || "", swift_code: bank?.swift_code || "",
    opening_balance: bank?.opening_balance || 0, is_default: bank?.is_default || false,
    notes: bank?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.bank_name.trim() || !f.account_number.trim()) { toast.error("Bank name and account number are required"); return; }
    setBusy(true);
    try {
      if (editing) await api.patch(`/t/finance/bank-accounts/${bank.id}`, f);
      else await api.post("/t/finance/bank-accounts", f);
      toast.success(editing ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/55 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-black/8">
          <div className="font-display font-bold text-xl">{editing ? "Edit bank account" : "New bank account"}</div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/5"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3.5">
          <Field lbl="Bank name *"><input value={f.bank_name} onChange={(e) => setF({ ...f, bank_name: e.target.value })} data-testid="fin-bank-form-bank" className="input" /></Field>
          <Field lbl="Account name *"><input value={f.account_name} onChange={(e) => setF({ ...f, account_name: e.target.value })} data-testid="fin-bank-form-name" className="input" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field lbl="Account number *" colSpan={2}><input value={f.account_number} onChange={(e) => setF({ ...f, account_number: e.target.value })} data-testid="fin-bank-form-number" className="input font-mono" /></Field>
            <Field lbl="Currency"><input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })} maxLength={4} className="input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field lbl="Branch"><input value={f.branch} onChange={(e) => setF({ ...f, branch: e.target.value })} className="input" /></Field>
            <Field lbl="SWIFT / IFSC"><input value={f.swift_code} onChange={(e) => setF({ ...f, swift_code: e.target.value })} className="input" /></Field>
          </div>
          <Field lbl="Opening balance"><input type="number" step="0.01" value={f.opening_balance} onChange={(e) => setF({ ...f, opening_balance: Number(e.target.value) })} className="input" /></Field>
          <label className="flex items-center gap-2.5 cursor-pointer"><input type="checkbox" checked={f.is_default} onChange={(e) => setF({ ...f, is_default: e.target.checked })} className="w-4 h-4" /><span className="text-sm text-black/85">Default deposit bank</span></label>
        </div>
        <div className="px-5 py-4 border-t border-black/8 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-black/5 text-black/65 font-bold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="fin-bank-form-save"
            className="px-5 py-2 rounded-xl bg-[#1FA868] text-white font-bold text-sm inline-flex items-center gap-1.5 disabled:opacity-60">
            <Save size={13} /> {busy ? "Saving…" : (editing ? "Save" : "Create")}
          </button>
        </div>
      </div>
      <style>{`.input{width:100%;padding:0.625rem 0.75rem;border-radius:0.75rem;border:1px solid rgba(0,0,0,0.12);background:white;font-size:0.875rem;outline:none}.input:focus{border-color:#1FA868}`}</style>
    </div>
  );
}

// ─────────── Account Mappings ───────────
function AccountMappings() {
  const [accounts, setAccounts] = useState([]);
  const [mappings, setMappings] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/t/finance/accounts").then((r) => r.data || []),
      api.get("/t/finance/mappings").then((r) => r.data || {}),
    ]).then(([a, m]) => { setAccounts(a); setMappings(m); });
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/t/finance/mappings", { mappings });
      toast.success("Mappings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const accountsByType = useMemo(() => {
    const m = {};
    for (const a of accounts) { (m[a.type] = m[a.type] || []).push(a); }
    return m;
  }, [accounts]);

  return (
    <div>
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-5 flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0"><ArrowRightLeft size={16} /></div>
        <div>
          <div className="font-bold text-sm text-amber-900">Map operational flows to ledger accounts</div>
          <div className="text-xs text-amber-800/85 mt-0.5">Tell Brivo which ledger to use whenever you record a sale, purchase or expense. This drives your reports & reconciliation.</div>
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-black/8 divide-y divide-black/6">
        {MAPPING_KEYS.map(({ k, lbl, hint, types }) => {
          const candidates = types.flatMap((t) => accountsByType[t] || []);
          return (
            <div key={k} className="p-4 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="font-bold text-black">{lbl}</div>
                <div className="text-xs text-black/55 mt-0.5">{hint} · accepts: {types.join(", ")}</div>
              </div>
              <select value={mappings[k] || ""} onChange={(e) => setMappings({ ...mappings, [k]: e.target.value || null })}
                data-testid={`fin-map-${k}`}
                className="w-72 px-3 py-2 rounded-xl border border-black/12 bg-white text-sm outline-none focus:border-[#1FA868]">
                <option value="">— Not mapped —</option>
                {candidates.map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
              </select>
            </div>
          );
        })}
      </div>
      <div className="mt-5 flex justify-end">
        <button onClick={save} disabled={busy} data-testid="fin-map-save"
          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#16834D] text-white font-bold text-sm inline-flex items-center gap-2 shadow disabled:opacity-60">
          <Save size={14} /> {busy ? "Saving…" : "Save mappings"}
        </button>
      </div>
    </div>
  );
}

// ─────────── Ledger History ───────────
function Ledger() {
  const [txns, setTxns] = useState(null);
  useEffect(() => { api.get("/t/finance/transactions").then((r) => setTxns(r.data || [])).catch(() => setTxns([])); }, []);
  return (
    <div className="bg-white rounded-2xl border border-black/8 overflow-hidden">
      {txns === null ? (
        <div className="p-14 text-center text-black/45">Loading…</div>
      ) : txns.length === 0 ? (
        <div className="p-14 text-center">
          <BookOpen size={28} className="mx-auto text-black/25 mb-2" />
          <div className="font-bold text-black">No ledger entries yet</div>
          <div className="text-xs text-black/55 mt-1">Sales, purchases and deposits will appear here once recorded against mapped accounts.</div>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-[#FAFBFB] text-[11px] font-bold uppercase tracking-wider text-black/55">
            <tr>
              <th className="text-left px-5 py-3">Date</th>
              <th className="text-left px-5 py-3">Description</th>
              <th className="text-left px-5 py-3">Account</th>
              <th className="text-right px-5 py-3">Debit</th>
              <th className="text-right px-5 py-3">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/6">
            {txns.map((t) => (
              <tr key={t.id}>
                <td className="px-5 py-3 text-black/55 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="px-5 py-3">{t.description}</td>
                <td className="px-5 py-3 font-mono">{t.account_code}</td>
                <td className="px-5 py-3 text-right font-mono">{t.debit ? `₦${Number(t.debit).toLocaleString()}` : ""}</td>
                <td className="px-5 py-3 text-right font-mono">{t.credit ? `₦${Number(t.credit).toLocaleString()}` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
